import React, { useEffect, useMemo, useRef, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import {
  useApproveInvestorInviteMutation,
  useDeleteInvestorsMutation,
  useReInviteInvestorMutation,
  useRejectInvestorInviteMutation,
} from "store/api/investors/investors.api"
import { useLazyGetInvestorsListQuery } from "store/api/investors/investors.api"
import moment from "moment"
import avatar from "../../assets/images/avatar.png"
import arrowDown from "../../assets/images/icons/arrow-down.svg"
import eye from "../../assets/images/icons/eye.svg"
import resend from "../../assets/images/icons/resend.svg"
import copy from "../../assets/images/icons/copy.svg"
import deleteIcon from "../../assets/images/icons/delete-red.svg"
import { Link, useNavigate } from "react-router-dom"
import DeleteConfirmationModal from "modals/deleteConfirmation"
import ReinviteConfirmationModal from "modals/reinviteConfirmation"
import { Button, Spinner, UncontrolledTooltip } from "reactstrap"
import {
  exportToExcel,
  getInvestorsStatus,
  getTableSortingHeaderIcon,
} from "helpers/common"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"
import { useSelector } from "react-redux"
import check from "../../assets/images/icons/right-check.svg"
import crossIcon from "../../assets/images/icons/close-icon-red.svg"
import toast from "react-hot-toast"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import { copyToClipboard, investorStatus } from "helpers/utils"
import "flatpickr/dist/themes/material_blue.css"
import FlatPickr from "react-flatpickr"

const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  status: "",
  created_at: "",
  timestamp: "",
}

const InvestorsTable = () => {
  const [getResults, { isFetching }] = useLazyGetInvestorsListQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [onDelete, { isLoading: isDeleting }] = useDeleteInvestorsMutation()
  const [onReinvite, { isLoading: isInviting }] = useReInviteInvestorMutation()
  const [isToast, setIsToast] = useState(false)
  const [isReinviteToast, setIsReinviteToast] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const deleteRef = useRef()
  const reinviteRef = useRef()
  const { hasPermission } = usePermission()
  const [approve] = useApproveInvestorInviteMutation()
  const [reject] = useRejectInvestorInviteMutation()
  const navigate = useNavigate()

  //meta title
  document.title = "List | Prachay"

  useEffect(() => {
    getList()
  }, [filters])

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  const onActionSuccess = () => {
    setIsToast(false)
    deleteRef.current = null
    setFilters((prev) => ({ ...prev, page: 1, timestamp: moment().unix() }))
  }

  const onApproveInvite = async (id) => {
    try {
      const { success, message } = await approve({
        id,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "InviteSuccess" })
        setFilters({ ...defaultFilters, timestamp: moment().unix() })
      }
    } catch (error) {
      toast.error(error?.data?.message, { id: "InviteError" })
    }
  }

  const onRejectInvite = async (id) => {
    try {
      const { success, message } = await reject({
        id,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "InviteSuccess" })
        setFilters({ ...defaultFilters, timestamp: moment().unix() })
      }
    } catch (error) {
      toast.error(error?.data?.message, { id: "InviteError" })
    }
  }

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Date"),
        accessorKey: "formatted_created_at",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        header: ({ column }) => getTableSortingHeaderIcon(column, "Name"),
        cell: ({ row }) => (
          <DefaultAvatar
            avatar_url={row?.original?.avatar_url}
            email={row?.original?.email}
            name={row?.original?.name}
          />
        ),
      },
      {
        header: "Code",
        accessorKey: "unique_code",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <>
            {row?.original.status === "APPROVED" ? (
              <div className="code-status">{row?.original.unique_code}</div>
            ) : (
              "—"
            )}
          </>
        ),
      },
      {
        header: "PAN",
        accessorKey: "pan_no",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {getInvestorsStatus(row?.original.status, row?.original.deleted_at)}
          </div>
        ),
      },
    ]

    const canSeeActions =
      hasPermission(PERMISSIONS.INVESTOR_VIEW) ||
      hasPermission(PERMISSIONS.INVESTOR_UPDATE) ||
      hasPermission(PERMISSIONS.INVESTOR_DELETE)

    if (canSeeActions) {
      baseColumns.push({
        id: "action",
        header: "Actions",
        cell: ({ row }) => {
          const resendTooltip = `resendTooltip-${row?.original.id}`
          const viewTooltip = `editTooltip-${row?.original.id}`
          const deleteTooltip = `deleteTooltip-${row?.original.id}`
          const copyTooltip = `copyTooltip-${row?.original.id}`

          if (row?.original?.status === "INVITE_REJECT") return null

          return (
            <div className="d-flex justify-content-center align-items-center gap-3">
              {hasPermission(PERMISSIONS.INVESTOR_UPDATE) && (
                <>
                  {(row?.original.status === "LINK_SENT" ||
                    row?.original.status === "LINK_EXPIRED") && (
                    <>
                      <div
                        className="action-delete"
                        onClick={() => {
                          reinviteRef.current = row?.original
                          setIsReinviteToast(true)
                        }}
                        id={resendTooltip}
                      >
                        <img src={resend} alt="" />
                        <UncontrolledTooltip
                          placement="top"
                          target={resendTooltip}
                        >
                          Resend Link
                        </UncontrolledTooltip>
                      </div>
                      {row?.original?.token &&
                        row?.original.status === "LINK_SENT" && (
                          <div
                            className="action-delete"
                            onClick={() => {
                              const url = `${process.env.REACT_APP_PUBLIC_URL}/verify-account/${row.original.token}`
                              if (url) {
                                copyToClipboard(url).then(() =>
                                  toast.success("Link Copied")
                                )
                              }
                            }}
                            id={copyTooltip}
                          >
                            <img src={copy} alt="" />
                            <UncontrolledTooltip
                              placement="top"
                              target={copyTooltip}
                            >
                              Copy Link
                            </UncontrolledTooltip>
                          </div>
                        )}
                    </>
                  )}
                </>
              )}

              {row.original.status === "INVITE_PENDING" ? (
                <>
                  {hasPermission(PERMISSIONS.INVESTOR_UPDATE) && (
                    <div className="action-view">
                      <div
                        className="action-delete"
                        onClick={() => {
                          onApproveInvite(row?.original?.id)
                        }}
                        id={viewTooltip}
                      >
                        <img width={14} height={14} src={check} alt="" />
                        <UncontrolledTooltip
                          placement="top"
                          target={viewTooltip}
                        >
                          Approve Invite
                        </UncontrolledTooltip>
                      </div>
                    </div>
                  )}
                  {hasPermission(PERMISSIONS.INVESTOR_UPDATE) && (
                    <div
                      className="action-delete"
                      onClick={() => {
                        onRejectInvite(row?.original?.id)
                      }}
                      id={deleteTooltip}
                    >
                      <img width={14} height={14} src={crossIcon} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={deleteTooltip}
                      >
                        Reject Invite
                      </UncontrolledTooltip>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {hasPermission(PERMISSIONS.INVESTOR_VIEW) && (
                    <div className="action-view">
                      <Link
                        to={
                          row?.original.status === "LINK_SENT" ||
                          row?.original.status === "LINK_EXPIRED"
                            ? `/investors/invite/${row?.original.id}`
                            : `/investors/view/${row?.original.id}`
                        }
                        id={viewTooltip}
                      >
                        <img src={eye} alt="" />
                        <UncontrolledTooltip
                          placement="top"
                          target={viewTooltip}
                        >
                          View
                        </UncontrolledTooltip>
                      </Link>
                    </div>
                  )}
                  {/* {hasPermission(PERMISSIONS.INVESTOR_DELETE) && (
                    <div
                      className="action-delete"
                      onClick={() => {
                        deleteRef.current = row?.original
                        setIsToast(true)
                      }}
                      id={deleteTooltip}
                    >
                      <img src={deleteIcon} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={deleteTooltip}
                      >
                        Delete
                      </UncontrolledTooltip>
                    </div>
                  )} */}
                </>
              )}
            </div>
          )
        },
      })
    }

    return baseColumns
  }, [hasPermission])

  const FilterComponents = () => (
    <>
      <div className="">
        <select
          className="form-select"
          style={{
            minWidth: "150px",
          }}
          value={filters.status}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              page: 1,
              status: e.target.value,
            }))
          }
        >
          <option value={""}>Select Status</option>
          {investorStatus.map((item, index) => (
            <option key={`investorStatus_${index}`} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>
      </div>
      <FlatPickr
        className={`form-control`}
        style={{
          maxWidth: "200px",
        }}
        placeholder="Select Date"
        options={{
          mode: "range",
          dateFormat: "d M, Y",
          maxDate: new Date(),
        }}
        value={filters.created_at}
        onChange={(date) => {
          if (date.length > 1) {
            setFilters((prev) => ({
              ...prev,
              page: 1,
              created_at: date,
            }))
          }
        }}
      />
      <Button className="btn btn-primary" onClick={() => handleExport()}>
        {excelLoading ? (
          <Spinner color={"#FFF"} size={"sm"} />
        ) : (
          <i className="mdi mdi-file-excel"></i>
        )}
      </Button>
      <Link className="btn btn-secondary" to={"/investors/invite"}>
        <i className="mdi mdi-plus me-1"></i> Invite
      </Link>
    </>
  )

  const handleExport = async () => {
    try {
      setExcelLoading(true)
      await exportToExcel(
        "/investors",
        { ...filters, exportToExcel: true },
        `Investors-${moment().format("D-MMM-YYYY")}.xlsx`
      )
    } catch (err) {
      toast.error("Export failed. Please try again.")
    } finally {
      setExcelLoading(false)
    }
  }

  return (
    <>
      <TableContainer
        isLoading={isFetching}
        count={count}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        data={data}
        FilterComponents={FilterComponents}
        isGlobalFilter={true}
        isCustomPageSize={true}
        // isGlobalFilter={true}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />

      {isToast && deleteRef && (
        <DeleteConfirmationModal
          open={isToast}
          id={deleteRef.current.id}
          setOpen={setIsToast}
          isLoading={isDeleting}
          onClose={() => {
            setIsToast(false)
            deleteRef.current = null
          }}
          title={"Delete Investor"}
          description="Are you sure you want to delete this investor?"
          onDelete={onDelete}
          onSuccess={onActionSuccess}
        />
      )}

      {isReinviteToast && reinviteRef && (
        <ReinviteConfirmationModal
          open={isReinviteToast}
          id={reinviteRef?.current?.id}
          setOpen={setIsReinviteToast}
          isLoading={isInviting}
          onClose={() => {
            setIsReinviteToast(false)
            reinviteRef.current = null
          }}
          title="Resend Email"
          description="Are you sure you want to send verification email again?"
          onSend={onReinvite}
          onSuccess={() => {
            setIsReinviteToast(false)
            setFilters((prev) => ({
              ...prev,
              page: 1,
              timestamp: moment().unix(),
            }))
            reinviteRef.current = null
          }}
        />
      )}
    </>
  )
}

export default InvestorsTable
